import { useCallback, useEffect, useRef, useState } from "react";
import { X, Sparkles } from "lucide-react";
import { API } from "../lib/api";
import MessageBubble from "./chat/MessageBubble";
import ChatInput from "./chat/ChatInput";
import { CHAT_SESSION_KEY, consumeChatStream } from "./chat/chatStream";

const HISTORY_KEY = "vv_chat_history";
const HISTORY_MAX_MESSAGES = 30;
const IS_DEV = typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production";

function loadHistory() {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]");
  } catch (err) {
    if (IS_DEV) console.warn("[chat] failed to parse history", err);
    return [];
  }
}
function saveHistory(msgs) {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(msgs.slice(-HISTORY_MAX_MESSAGES)));
  } catch (err) {
    if (IS_DEV) console.warn("[chat] failed to save history", err);
  }
}

function newId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(loadHistory);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => { saveHistory(messages); }, [messages]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);
  useEffect(() => () => { abortRef.current?.abort(); }, []);

  const send = useCallback(async (overrideText) => {
    const text = (typeof overrideText === "string" ? overrideText : input).trim();
    if (!text || streaming) return;
    if (!overrideText) setInput("");
    const userMsg = { id: newId("u"), role: "user", content: text };
    const asstMsg = { id: newId("a"), role: "assistant", content: "" };
    const asstId = asstMsg.id;
    setMessages((m) => [...m, userMsg, asstMsg]);
    setStreaming(true);

    // Only mutate the assistant bubble that owns this send() call. If the user
    // clicks "Start new chat" mid-stream the target message is gone and we
    // no-op instead of crashing on copy[-1].content.
    const appendDelta = (chunk) => setMessages((m) => {
      const idx = m.findIndex((x) => x.id === asstId);
      if (idx === -1) return m;
      const copy = [...m];
      copy[idx] = { ...copy[idx], content: copy[idx].content + chunk };
      return copy;
    });
    const replaceLast = (content) => setMessages((m) => {
      const idx = m.findIndex((x) => x.id === asstId);
      if (idx === -1) return m;
      const copy = [...m];
      copy[idx] = { ...copy[idx], content };
      return copy;
    });

    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;

    try {
      const session_id = localStorage.getItem(CHAT_SESSION_KEY) || null;
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id }),
        signal: controller.signal,
      });
      if (!resp.ok || !resp.body) throw new Error("Chat unavailable");
      await consumeChatStream(resp, {
        onDelta: appendDelta,
        onError: () => replaceLast("I hit an error. Please call (888) 773-8677 or try again in a moment."),
      }, controller.signal);
    } catch (e) {
      if (e?.name !== "AbortError") {
        replaceLast("I couldn't reach the assistant. Please call (888) 773-8677 or try again shortly.");
      }
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      setStreaming(false);
    }
  }, [input, streaming]);

  const resetChat = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    localStorage.removeItem(CHAT_SESSION_KEY);
    setMessages([]);
    setStreaming(false);
  }, []);

  return (
    <>
      {!open && (
        <button
          data-testid="chat-launcher"
          onClick={() => setOpen(true)}
          aria-label="Open peptide education assistant"
          className="fixed bottom-5 left-5 z-50 flex items-center gap-2 pl-3 pr-4 py-3 rounded-full bg-[color:var(--vv-onyx)] text-[color:var(--vv-gold)] shadow-[0_10px_30px_rgba(0,0,0,0.35)] hover:bg-[color:var(--vv-onyx-2)] transition-colors"
        >
          <span className="w-8 h-8 rounded-full bg-[color:var(--vv-gold)] text-[color:var(--vv-gold-deeper)] grid place-items-center">
            <Sparkles size={16} />
          </span>
          <span className="text-sm font-semibold">Education Assistant</span>
        </button>
      )}

      {open && (
        <div data-testid="chat-panel" className="fixed z-50 bottom-4 left-4 right-4 md:right-auto md:left-5 md:bottom-5 md:w-[400px] max-h-[80vh] flex flex-col rounded-2xl bg-white shadow-[0_20px_60px_rgba(0,0,0,0.25)] border border-stone-200 overflow-hidden">
          <div className="bg-[color:var(--vv-onyx)] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[color:var(--vv-gold)] text-[color:var(--vv-gold-deeper)] grid place-items-center">
                <Sparkles size={16} />
              </div>
              <div>
                <div className="font-display font-bold text-sm">Peptide Education Assistant</div>
                <div className="text-[10px] uppercase tracking-widest text-stone-400">Educational information only</div>
              </div>
            </div>
            <button data-testid="chat-close" onClick={() => setOpen(false)} className="text-stone-400 hover:text-white" aria-label="Close chat">
              <X size={18} />
            </button>
          </div>

          <div className="bg-amber-50 border-b border-amber-100 px-4 py-2 text-[11px] text-stone-700 leading-relaxed">
            Educational information only. Not a substitute for medical advice, diagnosis, or a prescription. A licensed VidaVital Medical provider evaluates every treatment.
          </div>

          <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto p-4 space-y-3 bg-[color:var(--vv-alt)] min-h-[280px]">
            {messages.length === 0 && (
              <div className="text-sm text-stone-600">
                <div className="font-medium text-stone-900 mb-1">Hello — how can I help with your peptide education?</div>
                Try: <em>"What is BPC-157?"</em>, <em>"Do I need bloodwork?"</em>, or <em>"How do I schedule a consultation?"</em>
              </div>
            )}
            {messages.map((m, i) => (
              <MessageBubble
                key={m.id || `${m.role}-${i}`}
                msg={m}
                index={i}
                isLastAssistant={m.role === "assistant" && i === messages.length - 1}
                streaming={streaming}
                onFollowup={send}
              />
            ))}
          </div>

          <ChatInput
            input={input}
            setInput={setInput}
            onSend={send}
            onReset={resetChat}
            streaming={streaming}
          />
        </div>
      )}
    </>
  );
}
